import type { Translate } from 'next-translate';
import React from 'react';
import { TextWithLinks } from '@/components/elements/TextWithLinks';
import { TrustpilotWidget } from '@/components/elements/TrustpilotWidget';
import SparkleIcon from '@/public/icons/pricing/sparkle.svg';
import { PRICING_FEATURES } from '../../data';
import styles from './PricingFeatures.module.scss';

interface Props {
	t: Translate;
	locale?: string;
	isTrialCredits?: boolean;
	hasTrialCredits?: boolean;
}

type CreditsVariant = 'full' | 'trial';

export const PricingFeatures: React.FC<Props> = ({
	t,
	locale,
	isTrialCredits = false,
	hasTrialCredits = false,
}) => {
	const creditsHighlights = {
		'{more}': (
			<span className={styles['features__credits-highlight']}>
				{t('credits_trialMore')}
			</span>
		),
		'{monthly}': (
			<span className={styles['features__credits-highlight']}>
				{t('credits_trialMonthly')}
			</span>
		),
	};

	const activeCredits: CreditsVariant = isTrialCredits ? 'trial' : 'full';
	const creditsVariants: CreditsVariant[] = hasTrialCredits
		? ['full', 'trial']
		: [activeCredits];

	const renderCredits = (variant: CreditsVariant) => {
		const isTrial = variant === 'trial';
		const isHidden = variant !== activeCredits;

		return (
			<div
				key={variant}
				className={styles['features__credits-layer']}
				data-hidden={isHidden || undefined}
				aria-hidden={isHidden || undefined}
			>
				<div className={styles['features__credits-info']}>
					<p className={styles['features__credits-title']}>
						{t(isTrial ? 'credits_trialTitle' : 'credits_title')}
					</p>
					<p className={styles['features__credits-subtitle']}>
						{isTrial ? (
							<TextWithLinks
								text={t('credits_trialSubtitle')}
								parts={creditsHighlights}
							/>
						) : (
							t('credits_subtitle')
						)}
					</p>
				</div>
				<p className={styles['features__credits-usage']}>
					{t(isTrial ? 'credits_trialImages' : 'credits_images')}
					<br />
					{t(isTrial ? 'credits_trialVideos' : 'credits_videos')}
				</p>
			</div>
		);
	};

	return (
		<div className={styles['features']}>
			<h2 className={styles['features__title']}>{t('features_title')}</h2>

			<div className={styles['features__credits']}>
				<SparkleIcon
					className={styles['features__credits-icon']}
					aria-hidden='true'
					focusable='false'
				/>
				<div className={styles['features__credits-layers']}>
					{creditsVariants.map(renderCredits)}
				</div>
			</div>

			<div className={styles['features__list']}>
				{PRICING_FEATURES.map(({ text, color, Icon }) => (
					<div
						key={text}
						className={`${styles['features__item']} ${
							styles[`features__item_${color}`]
						}`}
					>
						<span className={styles['features__icon']}>
							<Icon aria-hidden='true' focusable='false' />
						</span>
						<span className={styles['features__label']}>{t(text)}</span>
					</div>
				))}
			</div>

			{/*
				Live rating and review count come from Trustpilot's own TrustBox;
				the widget renders the card itself so the whole card is the link
			*/}
			<TrustpilotWidget
				locale={locale}
				className={styles['features__rating']}
			/>
		</div>
	);
};
